import { EventEmitter } from "events";
import { ElaborationMessage } from "../../redux/feature/object/remote-elaborations-slice";
import { v4 as uuid } from "uuid";

export type NotificationType = "none" | "success" | "error" | "warning" | "info" | "waiting";

export interface NotificationToast {
  key?: string;
  message: string;
  title: string;
  closable?: boolean;
  removeOnClose?: boolean;
  isTemporary?: boolean;
  duration?: number;
  type?: NotificationType;
  closed?: boolean;
  details?: ElaborationMessage[];
  onClose?: () => void;
  priority?: number;
  startShowing?: number;
  enableReloadPanel?: boolean;
}

class NotificationManager extends EventEmitter {
  listNotify: NotificationToast[];

  constructor() {
    super();
    this.listNotify = [];
  }

  addNotification(detail: NotificationToast) {
    const isAlreadyPresent = detail.key && this.listNotify.find((n) => n.key === detail.key) !== undefined;
    const shouldExpire =
      detail.isTemporary ?? (detail.duration !== undefined || detail.type === "success" || detail.type === "info");

    const factor = 1 + (this.listNotify.length > 0 ? 1 / Math.sqrt(this.listNotify.length + 1) : 0);
    if (shouldExpire) {
      const temporaryNotifications = this.listNotify.filter((n) => n.isTemporary);
      for (const notification of temporaryNotifications) {
        notification.duration = Math.floor(factor * notification.duration);
      }
    }

    const notification: NotificationToast = {
      key: detail.key ?? uuid(),
      type: detail.type || "info",
      startShowing: detail.startShowing ?? new Date().getTime(),
      closable: detail.closable ?? true,
      removeOnClose: true,
      ...detail,
      isTemporary: shouldExpire,
      duration: shouldExpire ? Math.floor(factor * (detail.duration ?? 5000)) : undefined,
    };

    if (notification.priority || isAlreadyPresent) {
      this.remove(notification.key, false);
      this.listNotify.unshift(notification);
    } else {
      this.listNotify.push(notification);
    }
    this.emitChange();
  }

  remove(key: string, update?: boolean) {
    this.listNotify = this.listNotify.filter((n) => key !== n.key);
    if (update === undefined || update === true) this.emitChange();
  }

  emitChange() {
    this.emit("CHANGE", this.listNotify);
  }

  addChangeListener(callback: (details: NotificationToast[]) => void) {
    this.addListener("CHANGE", callback);
  }

  removeChangeListener(callback: (details: NotificationToast[]) => void) {
    this.removeListener("CHANGE", callback);
  }

  updateStartShowing(key: string, timestamp: number) {
    const notification = this.listNotify.find((n) => n.key === key);
    if (notification) {
      notification.startShowing = timestamp;
    }
  }
}

export default new NotificationManager();
